/**
 * Rules behind the intake editor that need no React: variant lines, the
 * supplier combobox's rows, repeat-last-delivery prefill and the on-hand
 * preview in the confirm dialog. GRN/variants spec section 5.1.
 *
 * Kept here so they can be tested on their own; the editor only renders.
 */
import type {IntakeLine, StockIntake} from '../../internals/repository/StockIntakeRepository';
import type {Supplier} from '../../internals/repository/SupplierRepository';

export interface Variant {
  variantId: string;
  label: string;
}

/**
 * The variants a product line must choose between: only for a product
 * tracked per variant. `variants` is derived by the cloud; this never edits it.
 */
export function variantsOf(product: any): Variant[] {
  if (product?.trackByVariant !== true) return [];
  return Array.isArray(product?.variants) ? product.variants : [];
}

/** A line the user has actually filled in (a trailing blank is ignored). */
export const isFilled = (line: IntakeLine) =>
  line.kind === 'product' ? !!line.productId : !!line.stockId;

/**
 * Problems the editor can catch before the server does. Today that is the
 * variant: a line for a product tracked per variant must name one, and it
 * must be one of the product's variants.
 */
export function variantProblems(
  lines: IntakeLine[],
  productsById: Map<string, any>,
): {lineId: string; error: string}[] {
  const problems: {lineId: string; error: string}[] = [];
  for (const line of lines) {
    if (line.kind !== 'product' || !line.productId) continue;
    const variants = variantsOf(productsById.get(line.productId));
    if (variants.length === 0) continue;
    if (!line.variantId) {
      problems.push({lineId: line.lineId, error: 'Choose which variant was received'});
    } else if (!variants.some((v) => v.variantId === line.variantId)) {
      problems.push({lineId: line.lineId, error: 'That variant no longer exists; choose another'});
    }
  }
  return problems;
}

/** What the unit column says: the variant label for a variant line. */
export function unitLabel(
  line: IntakeLine,
  productsById: Map<string, any>,
  ingredientUnit: (stockId: string) => string,
): string {
  if (line.kind === 'ingredient') return ingredientUnit(line.stockId ?? '');
  const variant = variantsOf(productsById.get(line.productId ?? '')).find(
    (v) => v.variantId === line.variantId,
  );
  return variant?.label ?? line.variantLabel ?? 'unit';
}

// ─── Supplier combobox ───────────────────────────────────────────────────────

export type ComboRow =
  | {kind: 'supplier'; supplier: Supplier}
  | {kind: 'add'; name: string};

/**
 * The rows the supplier combobox shows for what has been typed: matching
 * active suppliers by name, then "Add '{text}' as a new supplier" when the
 * text is not already an exact name. The server's `q` is a prefix search;
 * this also matches inside a name, so "bev" finds "Lanka Bev".
 */
export function comboRows(suppliers: Supplier[], text: string, limit = 8): ComboRow[] {
  const needle = text.trim().toLowerCase();
  const active = suppliers.filter((s) => s.isActive !== false);
  const matches = needle
    ? active.filter((s) => s.name.toLowerCase().includes(needle))
    : active;

  const rows: ComboRow[] = matches
    .slice()
    .sort((a, b) => {
      // Names that START with the text first, then alphabetical.
      const ap = a.name.toLowerCase().startsWith(needle) ? 0 : 1;
      const bp = b.name.toLowerCase().startsWith(needle) ? 0 : 1;
      return ap - bp || a.name.localeCompare(b.name);
    })
    .slice(0, limit)
    .map((supplier) => ({kind: 'supplier' as const, supplier}));

  const exact = active.some((s) => s.name.trim().toLowerCase() === needle);
  if (needle && !exact) rows.push({kind: 'add', name: text.trim()});
  return rows;
}

/** Keyboard movement through the rows, wrapping at both ends. */
export function moveHighlight(current: number, delta: number, count: number): number {
  if (count === 0) return -1;
  if (current < 0) return delta > 0 ? 0 : count - 1;
  return (current + delta + count) % count;
}

// ─── Repeat last delivery ────────────────────────────────────────────────────

/**
 * Merge a supplier's last delivery into the lines on screen.
 *
 * Blank rows are simply replaced. Rows the user has filled in are never
 * overwritten: the prefill is APPENDED after them, and `needsConfirm` says so
 * the editor asks first. Each prefilled line gets a fresh lineId, since the
 * server derives event ids from it.
 */
export function applyPrefill(
  current: IntakeLine[],
  prefill: IntakeLine[],
  newLineId: () => string,
): {lines: IntakeLine[]; needsConfirm: boolean} {
  const kept = current.filter(isFilled);
  const incoming = prefill.map((l) => ({
    lineId: newLineId(),
    kind: l.kind,
    productId: l.kind === 'product' ? l.productId ?? null : null,
    stockId: l.kind === 'ingredient' ? l.stockId ?? null : null,
    variantId: l.variantId ?? null,
    variantLabel: l.variantLabel ?? null,
    qty: Number(l.qty) || 0,
    unitCost: Number(l.unitCost) || 0,
  }));
  return {lines: [...kept, ...incoming], needsConfirm: kept.length > 0};
}

// ─── Confirm dialog ──────────────────────────────────────────────────────────

/**
 * What a line's target holds now, and what it will hold once this posts —
 * the preview in the Post / Receive now confirmation. Null when the target
 * has never been counted (there is no meaningful "now").
 */
export function projectedOnHand(
  line: IntakeLine,
  productsById: Map<string, any>,
  ingredientsById: Map<string, any>,
): {now: number; after: number} | null {
  const qty = Number(line.qty) || 0;
  if (line.kind === 'ingredient') {
    const i = ingredientsById.get(line.stockId ?? '');
    if (!i || i.stockOnHand == null) return null;
    return {now: Number(i.stockOnHand) || 0, after: (Number(i.stockOnHand) || 0) + qty};
  }
  const p = productsById.get(line.productId ?? '');
  if (!p) return null;
  if (line.variantId) {
    const entry = p.variantStock?.[line.variantId];
    const now = entry ? Number(entry.onHand) || 0 : 0;
    return {now, after: now + qty};
  }
  if (p.stockOnHand == null) return null;
  return {now: Number(p.stockOnHand) || 0, after: (Number(p.stockOnHand) || 0) + qty};
}

/** How a posted document came to be posted, for the header. */
export function postedViaLabel(postedVia?: string | null): string | null {
  if (postedVia === 'receive_now') return 'Received directly';
  if (postedVia === 'draft') return 'Posted from draft';
  if (postedVia === 'import') return 'Imported';
  return null;
}

/** Documents created with Receive now, rather than posted from a draft. */
export const receivedDirectly = (intake: StockIntake) => intake.postedVia === 'receive_now';
